/**
 * "My Saved Reports" list for the Spatial Analysis drawer.
 *
 * Reports are user-scoped (see spatialAnalysisReportService). The durable
 * backend may not be deployed yet, so when the service reports it is
 * unavailable we render a friendly "under construction" placeholder rather than
 * an error.
 */

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ConstructionIcon from '@mui/icons-material/Construction';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import {
  ReportsUnavailableError,
  SavedReport,
  SavedReportSummary,
  getSpatialAnalysisReport,
  listSpatialAnalysisReports,
  renameSpatialAnalysisReport,
} from '../../../services/spatialAnalysis/spatialAnalysisReportService';

type Status = 'loading' | 'ready' | 'unavailable' | 'error';

const LIFTANGO_ORANGE = '#EE7334';

export default function SpatialAnalysisSavedReports({
  refreshToken,
  onOpenReport,
}: {
  refreshToken: number;
  onOpenReport: (report: SavedReport) => void;
}): React.ReactElement {
  const { t } = useTranslation('map');
  const [status, setStatus] = useState<Status>('loading');
  const [reports, setReports] = useState<SavedReportSummary[]>([]);
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);

  const handleOpen = async (reportId: string) => {
    setOpeningId(reportId);
    setOpenError(false);
    try {
      const full = await getSpatialAnalysisReport(reportId);
      onOpenReport(full);
    } catch {
      setOpenError(true);
    } finally {
      setOpeningId(null);
    }
  };

  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState('');
  const [savingRename, setSavingRename] = useState(false);
  const [renameError, setRenameError] = useState(false);

  const startRename = (report: SavedReportSummary) => {
    setEditingId(report.report_id);
    setDraftTitle(report.title);
    setRenameError(false);
  };

  const cancelRename = () => {
    setEditingId(null);
    setDraftTitle('');
  };

  const commitRename = async (reportId: string) => {
    const title = draftTitle.trim();
    if (!title) {
      return;
    }
    setSavingRename(true);
    setRenameError(false);
    try {
      await renameSpatialAnalysisReport(reportId, title);
      setReports((prev) => prev.map((r) => (r.report_id === reportId ? { ...r, title } : r)));
      setEditingId(null);
      setDraftTitle('');
    } catch {
      setRenameError(true);
    } finally {
      setSavingRename(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    listSpatialAnalysisReports()
      .then((list) => {
        if (!cancelled) {
          setReports(list);
          setStatus('ready');
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setStatus(error instanceof ReportsUnavailableError ? 'unavailable' : 'error');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  if (status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
        <CircularProgress size={18} sx={{ color: LIFTANGO_ORANGE }} />
      </Box>
    );
  }

  if (status === 'unavailable') {
    return (
      <Alert icon={<ConstructionIcon fontSize="inherit" />} severity="info" sx={{ mt: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {t('spatialAnalysis.savedReports.underConstructionTitle')}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t('spatialAnalysis.savedReports.underConstructionBody')}
        </Typography>
      </Alert>
    );
  }

  if (status === 'error') {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {t('spatialAnalysis.savedReports.loadError')}
      </Alert>
    );
  }

  if (reports.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {t('spatialAnalysis.savedReports.empty')}
      </Typography>
    );
  }

  return (
    <Box>
      {(openError || renameError) && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t(renameError ? 'spatialAnalysis.savedReports.renameError' : 'spatialAnalysis.savedReports.openError')}
        </Alert>
      )}
      <List dense disablePadding>
        {reports.map((report) =>
          editingId === report.report_id ? (
            <Box key={report.report_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5 }}>
              <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary', mr: 0.5 }} />
              <TextField
                value={draftTitle}
                onChange={(event) => setDraftTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    void commitRename(report.report_id);
                  } else if (event.key === 'Escape') {
                    cancelRename();
                  }
                }}
                size="small"
                variant="standard"
                fullWidth
                autoFocus
                disabled={savingRename}
                inputProps={{ 'aria-label': t('spatialAnalysis.savedReports.rename') }}
              />
              <Tooltip title={t('spatialAnalysis.savedReports.confirmRename')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={() => void commitRename(report.report_id)}
                    disabled={savingRename || !draftTitle.trim()}
                    aria-label={t('spatialAnalysis.savedReports.confirmRename')}
                  >
                    {savingRename ? <CircularProgress size={16} sx={{ color: LIFTANGO_ORANGE }} /> : <CheckIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('spatialAnalysis.savedReports.cancelRename')}>
                <span>
                  <IconButton
                    size="small"
                    onClick={cancelRename}
                    disabled={savingRename}
                    aria-label={t('spatialAnalysis.savedReports.cancelRename')}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <ListItemButton
              key={report.report_id}
              onClick={() => handleOpen(report.report_id)}
              disabled={openingId === report.report_id}
              sx={{ borderRadius: 1, px: 1 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {openingId === report.report_id ? (
                  <CircularProgress size={16} sx={{ color: LIFTANGO_ORANGE }} />
                ) : (
                  <DescriptionIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                )}
              </ListItemIcon>
              <ListItemText
                primary={report.title}
                secondary={report.created_at ? new Date(report.created_at).toLocaleString() : undefined}
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
              <Tooltip title={t('spatialAnalysis.savedReports.rename')}>
                <IconButton
                  size="small"
                  edge="end"
                  onClick={(event) => {
                    event.stopPropagation();
                    startRename(report);
                  }}
                  aria-label={t('spatialAnalysis.savedReports.rename')}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <OpenInFullIcon fontSize="small" sx={{ color: 'text.disabled', ml: 0.5 }} />
            </ListItemButton>
          )
        )}
      </List>
    </Box>
  );
}
